import { Box } from '@mui/material';
import { getBusinessesSummary } from 'lib/api/dashboard';
import { ParsedBusinessData } from 'lib/api/dashboard/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Pagination } from 'lib/types/shared';
import React from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

const tableHeaders: Header[] = [
  { key: 'name', label: 'Nombre' },
  { key: 'identifier', label: 'Rut' },
  { key: 'statusLabel', label: 'Estado SII', type: 'status-chip' },
  {
    key: 'averageSalesLast6Months',
    label: 'Prom. Venta U6M',
    type: 'money',
  },
  {
    key: 'averageSalesLast12Months',
    label: 'Prom. Venta U12M',
    type: 'money',
  },
  {
    key: 'creditNotesPercentage',
    label: '% NC',
    type: 'percentage',
  },
  { key: 'creditNotesAmount', label: 'Monto NC', type: 'money' },
  { key: 'cancellationPercentage', label: '% Cancel.', type: 'percentage' },
  { key: 'cancellationAmount', label: 'Monto Cancel.', type: 'money' },
  { key: 'fiscalDebtDue', label: 'Deuda Fiscal', type: 'money' },
  { key: 'crossInvoiceTotal', label: 'Tot. Fact. Cruzadas', type: 'money' },
  { key: 'tgrAlert', label: 'Alerta TGR', type: 'alert' },
];

const ClientsSummary = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ParsedBusinessData[]>([]);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [identifier, setIdentifier] = useState<string | null>(null);

  const buildQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (identifier) query += `businessIdentifier=${identifier}&`;
    if (businessName) query += `businessName=${businessName}&`;
    if (sortBy) query += `sortBy=${sortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchBusinessesSummary = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      setLoading(true);
      const query = buildQuery({ page, pageSize });
      const response = await getBusinessesSummary(dispatch, query);
      setData(response?.data || []);
      setPagination(response?.pagination || BASE_PAGINATION);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const filters: Filter[] = [
    {
      label: 'Cliente',
      type: 'autocomplete',
      value: identifier,
      key: 'walletBusiness',
      setter: setIdentifier,
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() =>
            fetchBusinessesSummary({
              page: pagination.page,
              pageSize: pagination.pageSize,
            })
          }
          selectedSortBy={sortBy}
          setSelectedSortBy={setSortBy}
          sortOptions={[{ value: 'createdAt', label: 'Creación' }]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
  ];

  useEffect(() => {
    void fetchBusinessesSummary({ page: 1, pageSize: 10 });
  }, []);

  return (
    <Box>
      <HeaderWithActions
        title="Acá podrás ver el resumen de tus clientes."
        variant="body1"
        buttons={buttons}
      />
      <TableWithPagination
        data={data}
        setData={setData}
        dataHeaders={tableHeaders}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchBusinessesSummary}
        loading={loading}
        setLoading={setLoading}
        exportToFile="xlsx"
        lastElement
      />
    </Box>
  );
};

export default ClientsSummary;
